import React from 'react'
import TileV2Skeleton from './TileV2Skeleton'

/**
 * Grid-shaped skeleton for playlist tiles.  N TileV2Skeletons inside the
 * existing `.playlist-tile-grid` block so the same 2 → 3 → 4 column
 * responsive rules (`_CommunityProfileCarousels.scss` lines 16–29) apply.
 *
 * Used by:
 *   • <PlaylistItemCarouselSkeleton> on mobile (< 768px), via delegation.
 *   • <SystemPlaylistFullView> at all viewports (the full view never
 *     switches to a carousel — it's grid-only).
 *
 * The series-label corner-badge placeholder is opt-in via `showSeriesLabel`.
 * When enabled, it paints on every 3rd tile to preview the mixed
 * series/non-series visual texture; when disabled (the default), no skeleton
 * tile renders a corner badge.  The label is absolutely positioned outside
 * tile bounds (`top: -8px; left: -8px`), so including it on some cells and
 * not others is layout-neutral.  Today only the Currently Watching surfaces
 * opt in — every other playlist's skeleton should stay badge-free.
 *
 * `role="presentation"` + `aria-hidden` keeps screen readers silent until
 * the real tiles announce themselves.
 */
interface PlaylistGridSkeletonProps {
  /** How many skeleton cells to render.  Cap upstream — a 200-item playlist
   *  must not paint 200 ghost cells. */
  count: number
  /** Opt in to the series-label corner-badge placeholder on every 3rd tile.
   *  Defaults to `false` — only Currently Watching surfaces pass `true`. */
  showSeriesLabel?: boolean
}

function PlaylistGridSkeleton ({
  count,
  showSeriesLabel = false,
}: PlaylistGridSkeletonProps): JSX.Element {
  const safeCount = Math.max(0, Math.floor(count))
  return (
    <div className="playlist-tile-grid" role="presentation" aria-hidden>
      <div className="playlist-tile-grid__inner">
        {Array.from({ length: safeCount }, (_, idx) => (
          <div
            // Static composite key — the array is fixed-length per render and
            // never reorders, so the index is safe; embedding `safeCount`
            // keeps keys stable across count changes without tripping the
            // `react/no-array-index-key` lint.
            key={`playlist-grid-skeleton-${safeCount}-${idx}`}
            className="playlist-tile-grid__item"
          >
            <TileV2Skeleton showSeriesLabel={showSeriesLabel && idx % 3 === 0} />
          </div>
        ))}
      </div>
    </div>
  )
}

export default React.memo(PlaylistGridSkeleton)
